import React, { useCallback, useEffect, useRef, useState } from "react";
// Use a plain anchor for the logo to avoid requiring router context in auth pages/tests
import {
  Eye,
  EyeOff,
  Lock,
  Mail,
  AlertCircle,
  ShieldCheck,
  Loader2,
  UserRound,
} from "lucide-react";
import { getAppLogoSrc, getAppLogoSizeStyle } from "../../constants/branding";
import {
  checkUsernameAvailability,
  GENERIC_IDENTIFIER_SIGN_UP_FAILURE_MESSAGE,
  EMAIL_ALREADY_EXISTS_MESSAGE,
  setPostAuthRedirect,
  signInWithIdentifier,
  signInWithGoogle,
  signUpWithEmail,
  USERNAME_AVAILABLE_MESSAGE,
  USERNAME_REQUIREMENTS_MESSAGE,
  USERNAME_TAKEN_MESSAGE,
  updateCurrentUserDisplayName,
} from "../../lib/auth";
import { createPendingCheckoutSession, fetchPendingCheckoutStatus, type PendingCheckoutStatus } from "../../lib/billing";
import { getBranding } from "../../config/branding";
import { currentRuntime, isFeatureEnabled } from "../../config/runtime";
import { resolveSidebarCollapsedLogo, resolveThemeAwarePrimaryLogo, type WhiteLabelAppBranding } from "../../lib/whiteLabelBranding";
import {
  buildLoginStripeGradient,
  cacheLogoAccentColors,
  extractLogoAccentColorsFromUrl,
  getCachedLogoAccentColors,
  hasCachedLogoAccentColors,
  type LogoAccentColors,
} from "../../lib/logoColors";
import { getTextLogoName } from "../../lib/textLogo";

type LoginPageProps = {
  initialMode?: "login" | "signup";
  onBackToLanding?: () => void;
  onForgotPassword?: () => void;
  authErrorMessage?: string | null;
  authSuccessMessage?: string | null;
  noticeMessage?: string | null;
  authInProgress?: boolean;
  checkoutHandoffActive?: boolean;
  onAuthIntent?: () => void;
  onAuthAttemptFailed?: () => void;
  onSignupCheckoutStart?: () => void;
  onSignupCheckoutEnd?: () => void;
  onCheckoutRedirectStart?: () => void;
  onAuthSuccess?: (session: { access_token?: string } | null) => void;
  whiteLabel?: WhiteLabelAppBranding | null;
  publicRegistrationEnabled?: boolean | null;
};

const CHECKOUT_BANNER_TITLE = "Preparing secure checkout…";
const CHECKOUT_BANNER_INITIAL_SUBTEXT = "Redirecting you to payment.";
const CHECKOUT_BANNER_DELAYED_SUBTEXT = "Still working… almost ready.";
const CHECKOUT_BANNER_FINAL_SUBTEXT = "Finalizing secure checkout…";
const CHECKOUT_BANNER_DELAY_MS = 3000;
const CHECKOUT_BANNER_FINAL_MS = 6500;
const USERNAME_CHECK_DEBOUNCE_MS = 400;

type UsernameAvailabilityStatus = "idle" | "checking" | "available" | "taken";

const normalizeUsernameInput = (value: string) => String(value || "").trim();

const PASSWORD_REQUIREMENTS_MESSAGE = 'At least 6 characters, including 1 uppercase and 1 number.';

const buildSignupSummaryText = (monthlyPriceLabel: string | undefined, trialDaysValue: number | undefined, fallbackSummary: string) => {
  const priceLabel = String(monthlyPriceLabel || '').trim();
  if (!priceLabel) {
    return String(fallbackSummary || '').trim();
  }

  const normalizedTrialDays = Number.isFinite(Number(trialDaysValue))
    ? Math.max(0, Math.trunc(Number(trialDaysValue)))
    : null;

  if (normalizedTrialDays && normalizedTrialDays > 0) {
    const dayLabel = normalizedTrialDays === 1 ? 'day' : 'days';
    return `Free for ${normalizedTrialDays} ${dayLabel}, then ${priceLabel}/month`;
  }

  return `${priceLabel}/month`;
};

const getPasswordValidationErrors = (password: string) => {
  const errs: string[] = [];
  if (!password || String(password).length < 6) errs.push('At least 6 characters');
  if (!/[A-Z]/.test(String(password))) errs.push('At least 1 uppercase letter');
  if (!/[0-9]/.test(String(password))) errs.push('At least 1 number');
  return errs;
};

const getUsernameValidationMessage = (value: string, requireValue: boolean = true) => {
  const normalizedValue = normalizeUsernameInput(value);
  if (!normalizedValue) {
    return requireValue ? "Username is required." : "";
  }

  return /^[A-Za-z0-9_-]{3,20}$/.test(normalizedValue) ? "" : USERNAME_REQUIREMENTS_MESSAGE;
};

const isUsernameTakenSignupError = (err: unknown) => {
  const message = String((err as { message?: string })?.message || "").trim().toLowerCase();
  const code = String((err as { code?: string })?.code || '').trim().toLowerCase();

  return code === 'username_taken'
    || message === USERNAME_TAKEN_MESSAGE.toLowerCase();
};

const isEmailAlreadyExistsSignupError = (err: unknown) => {
  const message = String((err as { message?: string })?.message || "").trim().toLowerCase();
  const code = String((err as { code?: string })?.code || "").trim().toLowerCase();

  return code === 'email_exists'
    || message === EMAIL_ALREADY_EXISTS_MESSAGE.toLowerCase();
};

const isPasswordValidationSignupError = (err: unknown) => {
  const message = String((err as { message?: string })?.message || "").trim().toLowerCase();
  const code = String((err as { code?: string })?.code || "").trim().toLowerCase();

  return code === 'invalid_password'
    || (message.includes('password') && (message.includes('at least') || message.includes('uppercase') || message.includes('number') || message.includes('length') || message.includes('min')));
};

  const tpStripeFlashLog = (event: string, payload: Record<string, unknown> = {}) => {
    try {
      const monoTs = typeof performance !== 'undefined' ? performance.now() : null;
      console.info('[TP-STRIPE-FLASH]', event, { ts: Date.now(), monoTs, ...payload });
    } catch {}
  };

export default function LoginPage({
  initialMode = "login",
  onForgotPassword,
  authErrorMessage = null,
  authSuccessMessage = null,
  noticeMessage = null,
  authInProgress = false,
  checkoutHandoffActive = false,
  onAuthIntent,
  onAuthAttemptFailed,
  onSignupCheckoutStart,
  onSignupCheckoutEnd,
  onCheckoutRedirectStart,
  onAuthSuccess,
  whiteLabel = null,
  publicRegistrationEnabled = null,
}: LoginPageProps) {
  const brand = currentRuntime.brand;
  const features = currentRuntime.features;
  const branding = getBranding(brand.id);
  const authCopy = branding.copy.auth;
  const usesCheckoutSignupFlow = (branding.signupFlow || 'checkout') === 'checkout';
  const usesFullSignupFields = brand?.id === 'warrior-trading' || brand?.id === 'trade-proof' || brand?.id === 'default' || !usesCheckoutSignupFlow;

  const checkoutBannerTitle = authCopy.checkoutBannerTitle || CHECKOUT_BANNER_TITLE;
  const checkoutBannerInitialSubtext = authCopy.checkoutBannerInitialSubtext || CHECKOUT_BANNER_INITIAL_SUBTEXT;
  const checkoutBannerDelayedSubtext = authCopy.checkoutBannerDelayedSubtext || CHECKOUT_BANNER_DELAYED_SUBTEXT;
  const checkoutBannerFinalSubtext = authCopy.checkoutBannerFinalSubtext || CHECKOUT_BANNER_FINAL_SUBTEXT;
  const signInLoadingTitle = authCopy.signInLoadingTitle || 'Loading workspace';
  const signInLoadingSubtitle = authCopy.signInLoadingSubtitle || 'Preparing your session';

  const showGoogleAuth = isFeatureEnabled(features, 'googleAuth');

  const logoSrc = whiteLabel?.tenantSlug === 'bearbull' || whiteLabel?.tenantSlug === 'mic'
    ? (whiteLabel?.tenantSlug === 'bearbull' ? whiteLabel.loadingPageLogoHref : null)
      || resolveSidebarCollapsedLogo(whiteLabel, 'dark')
      || resolveThemeAwarePrimaryLogo(whiteLabel, 'dark')
      || getAppLogoSrc({ brand: branding, variant: "horizontalSplit", theme: "dark" })
    : resolveThemeAwarePrimaryLogo(whiteLabel, 'dark')
      || getAppLogoSrc({ brand: branding, variant: "horizontalSplit", theme: "dark" });
  const logoStyle = {
    ...getAppLogoSizeStyle({ brand: branding, variant: "horizontalSplit" }),
    ...(brand.id === 'trade-proof' && (!whiteLabel?.tenantSlug || whiteLabel.tenantSlug === 'trade-proof')
      ? {
        transform: 'scale(1.15)',
        maxWidth: 'min(100%, clamp(240px, 24vw, 320px))',
      }
      : whiteLabel?.tenantSlug === 'warrior-trading'
      ? { transform: 'scale(1.4)' }
      : whiteLabel?.tenantSlug === 'ztradez'
      ? { transform: 'scale(1.2)' }
      : whiteLabel?.tenantSlug === 'bows'
        ? { transform: 'scale(1.6)' }
      : whiteLabel?.tenantSlug === 'pjtrades'
        ? { transform: 'scale(0.8)' }
      : whiteLabel?.tenantSlug === 'candlesticks'
        ? { transform: 'scale(1.15)' }
      : whiteLabel?.tenantSlug === 'trading-with-rustyrod'
        ? { transform: 'scale(1.15)' }
      : whiteLabel?.tenantSlug === 'bearbull'
        ? { width: '234.02px', height: '110px', transform: 'none' }
      : whiteLabel?.tenantSlug === 'pbalerts'
        ? { transform: 'scale(0.8)' }
      : whiteLabel?.tenantSlug === 'mission-winners'
        ? { transform: 'scale(0.8)' }
      : whiteLabel?.tenantSlug === 'demontrading'
        ? { transform: 'scale(0.75)' }
      : whiteLabel?.tenantSlug === 'fullport-university'
        ? { transform: 'scale(1.61)' }
      : whiteLabel?.tenantSlug === 'investors-underground'
        ? { transform: 'scale(0.94)' }
      : whiteLabel?.tenantSlug === 'stock-navigators'
          ? { transform: 'scale(0.85)' }
           : whiteLabel?.tenantSlug === 'team-bull-trading'
          ? { transform: 'scale(1.25)' }
                : whiteLabel?.tenantSlug === 'pb-trading'
          ? { transform: 'scale(0.85)' }
        : whiteLabel?.tenantSlug === 'prop-firm-kid'
          ? { transform: 'scale(0.85)' }
                  : whiteLabel?.tenantSlug === 'profit-raja'
          ? { transform: 'scale(1.25)', marginTop: '10px' }
                : whiteLabel?.tenantSlug === 'the-noir-exchange'
          ? { transform: 'scale(0.85)', marginTop: '-5px', marginBottom: '10px' }
        : whiteLabel?.tenantSlug === 'bullbear-den'
          ? { transform: 'scale(1.8)', marginTop: '30px', marginBottom: '50px' }
        : whiteLabel?.tenantSlug === 'stock-vip'
          ? { width: '130.35px', height: '126.175px', transform: 'none' }
        : whiteLabel?.tenantSlug === 'gold-pips'
          ? { transform: 'scale(1.15)' }
        : whiteLabel?.tenantSlug === 'marketpulse'
          ? { transform: 'scale(1.2)', marginTop: '10px' }
        : whiteLabel?.tenantSlug === 'gem-futures'
          ? { transform: 'scale(1.1)' }
        : {}),
  };
  const isCharan = whiteLabel?.tenantSlug === 'charan';
  const loginPrimaryColor = whiteLabel?.primaryColor || (isCharan ? '#800490' : '#138c42');
  const isInvestorsUnderground = whiteLabel?.tenantSlug === 'investors-underground';
  const isBearBull = whiteLabel?.tenantSlug === 'bearbull';
  const isBullishBears = whiteLabel?.tenantSlug === 'bullish-bears';
  const isTackleTrading = whiteLabel?.tenantSlug === 'tackletrading';
  const isBows = whiteLabel?.tenantSlug === 'bows';
  const isStockVip = whiteLabel?.tenantSlug === 'stock-vip';
  const isPjTrades = whiteLabel?.tenantSlug === 'pjtrades';
  const isMic = whiteLabel?.tenantSlug === 'mic';
  const isAmericanDreamTrading = whiteLabel?.tenantSlug === 'american-dream-trading';
  const isBullBearDen = whiteLabel?.tenantSlug === 'bullbear-den';
  const isProfitRaja = whiteLabel?.tenantSlug === 'profit-raja';
  const LogoWrapper = isMic || isBearBull ? 'div' : 'a';
  const americanDreamPrimaryColor = '#c29800';
  const primaryLogoUrl = String(whiteLabel?.logoHref || '').trim();
  const isFirstPartyTradeProof = brand.id === 'trade-proof' && (!whiteLabel?.tenantSlug || whiteLabel.tenantSlug === 'trade-proof');
  const tradeProofPrimaryColor = '#108e75';
  const tradeProofLinkColor = '#138c42';
  const micAccentColor = `color-mix(in srgb, ${loginPrimaryColor} 78%, white)`;
  const portalAccentColor = brand.id === 'warrior-trading'
    ? '#0da6d8'
    : whiteLabel?.tenantSlug === 'fullport-university'
      ? '#6692f0'
    : isInvestorsUnderground
      ? '#194dbe'
      : isMic
      ? micAccentColor
      : loginPrimaryColor;

  const legacyLoginStripeBackground = isTackleTrading
    ? 'linear-gradient(90deg, #0b1f3a 0%, #0b1f3a 30%, #f59e0b 50%, #0b1f3a 70%, #0b1f3a 100%)'
    : isProfitRaja
      ? 'linear-gradient(90deg, #555656 0%, #a7a7a8 22%, #e5e7eb 38%, #ffffff 50%, #d4af37 62%, #f3d36a 78%, #8f6b1f 100%)'
    : isPjTrades
      ? 'linear-gradient(90deg, #ef1b24 0%, #ef1b24 35%, #ffffff 50%, #ef1b24 65%, #ef1b24 100%)'
      : whiteLabel?.tenantSlug === 'trading-with-rustyrod'
        ? 'linear-gradient(90deg, #0b1f3a 0%, #0b1f3a 8%, #00c8f5 42%, #ffffff 50%, #e91e8f 58%, #e91e8f 92%, #0b1f3a 100%)'
      : whiteLabel?.tenantSlug === 'candlesticks'
        ? 'linear-gradient(90deg, #b7791f 0%, #b7791f 35%, #f5e6c8 50%, #b7791f 65%, #b7791f 100%)'
      : whiteLabel?.tenantSlug === 'jdub'
        ? 'linear-gradient(90deg, #2f80ed 0%, #2f80ed 35%, #ffffff 50%, #d99b2b 65%, #d99b2b 100%)'
      : whiteLabel?.tenantSlug === 'mission-winners'
        ? 'linear-gradient(90deg, #5b8f3c 0%, #5b8f3c 35%, #ffffff 50%, #5b8f3c 65%, #5b8f3c 100%)'
      : whiteLabel?.tenantSlug === 'demontrading'
        ? 'linear-gradient(90deg, #e87500 0%, #e87500 25%, #ffffff 40%, #ffffff 60%, #e87500 75%, #e87500 100%)'
      : whiteLabel?.tenantSlug === 'her'
        ? 'linear-gradient(90deg, #8f4657 0%, #8f4657 35%, #e9b7c0 50%, #8f4657 65%, #8f4657 100%)'
      : brand.id === 'warrior-trading'
        ? 'linear-gradient(90deg, #0da6d8 0%, #0da6d8 33%, #ffffff 50%, #035a90 67%, #014c7a 100%)'
      : isBearBull
        ? 'linear-gradient(90deg, #991b1e 0%, #991b1e 38%, #ffffffa6 50%, #991b1e 62%, #991b1e 100%)'
      : isBullishBears
        ? 'linear-gradient(90deg, #f07c00 0%, #f07c00 35%, #ffffffa6 55%, #f07c00 65%, #f07c00 100%)'
      : isInvestorsUnderground
        ? `linear-gradient(90deg, ${loginPrimaryColor} 0%, ${loginPrimaryColor} 39%, color-mix(in srgb, ${loginPrimaryColor} 55%, white) 50%, ${loginPrimaryColor} 61%, ${loginPrimaryColor} 100%)`
      : isMic
        ? `linear-gradient(90deg, ${micAccentColor} 0%, ${micAccentColor} 40%, color-mix(in srgb, ${micAccentColor} 55%, white) 50%, ${micAccentColor} 60%, ${micAccentColor} 100%)`
      : null;
  const shouldUseAutomaticLogoStripe = !legacyLoginStripeBackground;
  const defaultLoginStripeBackground = 'linear-gradient(90deg, #1e40af 0%, #bbf7d0 50%, #16a34a 100%)';

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [errorState, setErrorState] = useState("");
  const setError = (val: string) => {
    setErrorState(val);
  };
  const error = errorState;
  const [validationErrors, setValidationErrors] = useState<{ username?: string; email?: string; password?: string; confirmPassword?: string }>({});
  const [isSignUpMode, setIsSignUpMode] = useState(initialMode === "signup");
  const [pendingRecoveryStatus, setPendingRecoveryStatus] = useState<PendingCheckoutStatus | null>(null);
  const [pendingRecoveryCheckComplete, setPendingRecoveryCheckComplete] = useState(() => {
    try {
      return !sessionStorage.getItem('tt_pending_signup_id');
    } catch {
      return true;
    }
  });
  const signupCopy = authCopy;
  const signupSummaryText = buildSignupSummaryText(
    branding.copy.billing?.pricing?.monthly?.priceLabel,
    branding.copy.billing?.trialDays,
    signupCopy.signupSummary,
  );
  const [checkoutBannerSubtext, setCheckoutBannerSubtext] = useState(checkoutBannerInitialSubtext);
  const submitInFlightRef = useRef(false);
  const checkoutBannerTimerIdsRef = useRef<number[]>([]);
  const hasStartedHealthWarmupRef = useRef(false);
  const [usernameAvailability, setUsernameAvailability] = useState<{ status: UsernameAvailabilityStatus; username: string }>({
    status: "idle",
    username: "",
  });
  const [automaticLoginStripeColors, setAutomaticLoginStripeColors] = useState<LogoAccentColors | null>(
    () => shouldUseAutomaticLogoStripe ? getCachedLogoAccentColors(primaryLogoUrl) : null,
  );

  useEffect(() => {
    if (hasStartedHealthWarmupRef.current) return;
    hasStartedHealthWarmupRef.current = true;

    const apiBase = String((import.meta as any).env?.VITE_API_BASE || '').trim().replace(/\/+$/, '');
    const healthUrl = apiBase ? `${apiBase}/healthz` : '/healthz';

    void fetch(healthUrl, {
      method: 'GET',
      credentials: 'omit',
      cache: 'no-store',
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const cached = shouldUseAutomaticLogoStripe ? getCachedLogoAccentColors(primaryLogoUrl) : null;
    setAutomaticLoginStripeColors(cached);
    if (!shouldUseAutomaticLogoStripe || !primaryLogoUrl || hasCachedLogoAccentColors(primaryLogoUrl)) return;

    let cancelled = false;
    void extractLogoAccentColorsFromUrl(primaryLogoUrl).then((colors) => {
      if (cancelled) return;
      cacheLogoAccentColors(primaryLogoUrl, colors);
      setAutomaticLoginStripeColors(colors);
    });
    return () => {
      cancelled = true;
    };
  }, [primaryLogoUrl, shouldUseAutomaticLogoStripe]);

  const automaticLoginStripeBackground = buildLoginStripeGradient(automaticLoginStripeColors);
  const automaticBrandStripeBackground = buildLoginStripeGradient({
    primaryColor: loginPrimaryColor,
    secondaryColor: whiteLabel?.secondaryColor || null,
  });
  const loginStripeBackground = legacyLoginStripeBackground || automaticLoginStripeBackground || automaticBrandStripeBackground || defaultLoginStripeBackground;

  const passwordValidationErrors = getPasswordValidationErrors(password);
  const isPasswordValid = passwordValidationErrors.length === 0;
  const usernameAvailabilityTimerRef = useRef<number | null>(null);
  const usernameAvailabilityRequestRef = useRef(0);

  useEffect(() => {
    setIsSignUpMode(initialMode === "signup");
  }, [initialMode]);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    let pendingId = '';
    try { pendingId = sessionStorage.getItem('tt_pending_signup_id') || ''; } catch {}
    if (!pendingId) {
      setPendingRecoveryStatus(null);
      setPendingRecoveryCheckComplete(true);
      return () => { cancelled = true; };
    }
    setPendingRecoveryCheckComplete(false);
    const check = async () => {
      let pendingEmail = '';
      try { pendingEmail = sessionStorage.getItem('tt_pending_signup_email') || ''; } catch {}
      let status: PendingCheckoutStatus | null = null;
      try {
        status = typeof fetchPendingCheckoutStatus === 'function'
          ? await fetchPendingCheckoutStatus(pendingEmail)
          : null;
      } catch {}
      if (cancelled) return;
      if (status?.complete) {
        try { sessionStorage.removeItem('tt_pending_signup_id'); } catch {}
        setPendingRecoveryStatus(null);
        setPendingRecoveryCheckComplete(true);
        return;
      }
      setPendingRecoveryStatus(status);
      setPendingRecoveryCheckComplete(true);
      if (status?.provisioningInProgress) timer = window.setTimeout(check, 2000);
    };
    void check();
    return () => { cancelled = true; if (timer) window.clearTimeout(timer); };
  }, [email]);

  const clearCheckoutBannerTimers = useCallback(() => {
    for (const timerId of checkoutBannerTimerIdsRef.current) {
      window.clearTimeout(timerId);
    }
    checkoutBannerTimerIdsRef.current = [];
  }, []);

  const clearUsernameAvailabilityTimer = useCallback(() => {
    if (usernameAvailabilityTimerRef.current != null) {
      window.clearTimeout(usernameAvailabilityTimerRef.current);
      usernameAvailabilityTimerRef.current = null;
    }
  }, []);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextEmail = event.target.value;
    setEmail(nextEmail);
    setError("");
    setValidationErrors((current) => {
      if (!current.email) {
        return current;
      }

      const nextErrors = { ...current };
      delete nextErrors.email;
      return nextErrors;
    });
  };

  const showCheckoutProgressBanner = isSignUpMode && (isEmailLoading || checkoutHandoffActive);

  useEffect(() => {
    clearCheckoutBannerTimers();
    setCheckoutBannerSubtext(checkoutBannerInitialSubtext);

    if (!showCheckoutProgressBanner) {
      return;
    }

    checkoutBannerTimerIdsRef.current = [
      window.setTimeout(() => {
        setCheckoutBannerSubtext((current) => (
          current === checkoutBannerInitialSubtext
            ? checkoutBannerDelayedSubtext
            : current
        ));
      }, CHECKOUT_BANNER_DELAY_MS),
      window.setTimeout(() => {
        setCheckoutBannerSubtext(checkoutBannerFinalSubtext);
      }, CHECKOUT_BANNER_FINAL_MS),
    ];

    return () => {
      clearCheckoutBannerTimers();
    };
  }, [
    checkoutBannerDelayedSubtext,
    checkoutBannerFinalSubtext,
    checkoutBannerInitialSubtext,
    clearCheckoutBannerTimers,
    showCheckoutProgressBanner,
  ]);

  useEffect(() => {
    return () => {
      clearUsernameAvailabilityTimer();
    };
  }, [clearUsernameAvailabilityTimer]);

  const runUsernameAvailabilityCheck = useCallback(async (candidate: string) => {
    const normalizedCandidate = normalizeUsernameInput(candidate);
    if (!isSignUpMode) return;
    if (!normalizedCandidate || getUsernameValidationMessage(normalizedCandidate, false)) {
      return;
    }

    const requestId = usernameAvailabilityRequestRef.current + 1;
    usernameAvailabilityRequestRef.current = requestId;
    setUsernameAvailability({ status: "checking", username: normalizedCandidate });

    try {
      const result = await checkUsernameAvailability(normalizedCandidate);
      if (usernameAvailabilityRequestRef.current !== requestId) {
        return;
      }

      if (result.available) {
        setUsernameAvailability({ status: "available", username: normalizedCandidate });
        setValidationErrors((current) => {
          if (current.username !== USERNAME_TAKEN_MESSAGE) {
            return current;
          }

          const nextErrors = { ...current };
          delete nextErrors.username;
          return nextErrors;
        });
        return;
      }

      setUsernameAvailability({ status: "taken", username: normalizedCandidate });
      setValidationErrors((current) => ({
        ...current,
        username: USERNAME_TAKEN_MESSAGE,
      }));
    } catch {
      if (usernameAvailabilityRequestRef.current !== requestId) {
        return;
      }

      setUsernameAvailability({ status: "idle", username: normalizedCandidate });
    }
  }, [isSignUpMode]);

  useEffect(() => {
    clearUsernameAvailabilityTimer();
    usernameAvailabilityRequestRef.current += 1;

    if (!isSignUpMode) {
      setUsernameAvailability({ status: "idle", username: "" });
      return;
    }

    const normalizedCandidate = normalizeUsernameInput(username);
    if (!normalizedCandidate) {
      setUsernameAvailability({ status: "idle", username: "" });
      return;
    }

    if (getUsernameValidationMessage(normalizedCandidate, false)) {
      setUsernameAvailability({ status: "idle", username: normalizedCandidate });
      return;
    }

    usernameAvailabilityTimerRef.current = window.setTimeout(() => {
      void runUsernameAvailabilityCheck(normalizedCandidate);
    }, USERNAME_CHECK_DEBOUNCE_MS);

    return () => {
      clearUsernameAvailabilityTimer();
    };
  }, [clearUsernameAvailabilityTimer, isSignUpMode, runUsernameAvailabilityCheck, username]);

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextUsername = event.target.value;
    const normalizedCandidate = normalizeUsernameInput(nextUsername);

    setUsername(nextUsername);
    setError("");
    clearUsernameAvailabilityTimer();
    usernameAvailabilityRequestRef.current += 1;
    setUsernameAvailability({ status: "idle", username: normalizedCandidate });
    setValidationErrors((current) => {
      if (!current.username) {
        return current;
      }

      const nextErrors = { ...current };
      delete nextErrors.username;
      return nextErrors;
    });
  };

  const handleUsernameBlur = () => {
    const normalizedCandidate = normalizeUsernameInput(username);
    const validationMessage = getUsernameValidationMessage(normalizedCandidate, false);

    if (validationMessage) {
      if (!normalizedCandidate) {
        return;
      }

      setValidationErrors((current) => ({
        ...current,
        username: validationMessage,
      }));
      return;
    }

    if (
      usernameAvailability.username === normalizedCandidate
      && (usernameAvailability.status === "available" || usernameAvailability.status === "taken")
    ) {
      return;
    }

    clearUsernameAvailabilityTimer();
    void runUsernameAvailabilityCheck(normalizedCandidate);
  };

  const resolveSignupFlowErrorMessage = (err: unknown) => {
    const message = String((err as { message?: string })?.message || '').trim();
    const code = String((err as { code?: string })?.code || '').trim();
    if (!message || code === 'unexpected_failure' || message === 'Generic failure') {
      return GENERIC_IDENTIFIER_SIGN_UP_FAILURE_MESSAGE || authCopy.genericSignupFailureMessage;
    }

    if (message === 'missing_access_token' || message === 'missing_user_id') {
      return authCopy.checkoutFallbackMessage;
    }
    if (message === 'billing_not_configured') {
      return authCopy.billingNotConfiguredMessage;
    }

    return message;
  };

  const sanitizeAuthErrorForDisplay = (raw?: string | null) => {
    const message = String(raw || '').trim();
    if (!message) return message;
    const lower = message.toLowerCase();
    if (
      lower.includes('not assigned to the requested tenant') ||
      lower.includes('not active for the requested tenant') ||
      lower.includes('not authorized for') ||
      lower.includes('tenant_access_denied')
    ) {
      return 'Invalid email or password.';
    }
    return message;
  };

  const goToSignupMode = () => {
    tpStripeFlashLog('signup_mode_navigation_start', {
      currentPath: String(window.location.pathname || '') || null,
    });
    try {
      window.history.pushState({}, '', '/signup');
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch {}
    tpStripeFlashLog('signup_mode_navigation_complete', {
      currentPath: String(window.location.pathname || '') || null,
    });
    setIsSignUpMode(true);
  };

  const goToLoginMode = () => {
    tpStripeFlashLog('login_mode_navigation_start', {
      currentPath: String(window.location.pathname || '') || null,
    });
    clearCheckoutBannerTimers();
    setCheckoutBannerSubtext(checkoutBannerInitialSubtext);
    setError('');
    setValidationErrors({});
    onSignupCheckoutEnd?.();

    try {
      window.history.pushState({}, '', '/login');
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch {}

    setIsSignUpMode(false);
    tpStripeFlashLog('login_mode_navigation_complete', {
      currentPath: String(window.location.pathname || '') || null,
    });
  };

  const pinSignupSurface = () => {
    tpStripeFlashLog('signup_route_pin_start', {
      currentPath: String(window.location.pathname || '') || null,
    });
    try {
      if (String(window.location.pathname || '').trim() !== '/signup') {
        window.history.replaceState({}, '', '/signup');
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    } catch {}
    tpStripeFlashLog('signup_route_pin_complete', {
      currentPath: String(window.location.pathname || '') || null,
    });
    setIsSignUpMode(true);
  };

  const handleSubmit = async (e: React.FormEvent, forcedMode?: 'signup' | 'login') => {
    e.preventDefault();
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    tpStripeFlashLog('signup_submit_start', {
      mode: forcedMode ?? (isSignUpMode ? 'signup' : 'login'),
      path: String(window.location.pathname || '') || null,
      isSignUpMode,
      hasUsername: !!String(username || '').trim(),
      hasEmail: !!String(email || '').trim(),
    });
    setIsEmailLoading(true);
    setError("");
    setValidationErrors({});

    const mode = forcedMode ?? (isSignUpMode ? 'signup' : 'login');

    if (mode === 'signup' && !pendingRecoveryCheckComplete) {
      return;
    }

    if (mode === 'signup' && pendingRecoveryStatus?.checkoutCompleted && !pendingRecoveryStatus.provisioningFailed) {
      setError("Your trial was created and we're finishing your account setup. This usually takes only a moment.");
      return;
    }

    try {
      if (mode === 'signup') {
        // client-side validation
        const errors: { username?: string; email?: string; password?: string; confirmPassword?: string } = {};
        const normalizedUsername = normalizeUsernameInput(username);
        const usernameValidationMessage = getUsernameValidationMessage(normalizedUsername);
        if (usernameValidationMessage) errors.username = usernameValidationMessage;
        if (!errors.username && usernameAvailability.status === "taken" && usernameAvailability.username === normalizedUsername) {
          errors.username = USERNAME_TAKEN_MESSAGE;
        }
        if (!email || !email.trim()) errors.email = 'Email is required.';
        if (usesFullSignupFields) {
          if (!password) {
            errors.password = 'Password is required.';
          } else if (!isPasswordValid) {
            errors.password = PASSWORD_REQUIREMENTS_MESSAGE;
          }
          if (!confirmPassword) {
            errors.confirmPassword = 'Please confirm your password.';
          } else if (password !== confirmPassword) {
            errors.confirmPassword = 'Passwords do not match.';
          }
        }

        if (Object.keys(errors).length > 0) {
          tpStripeFlashLog('signup_validation_failed', {
            path: String(window.location.pathname || '') || null,
            validationErrors: Object.keys(errors),
          });
          setValidationErrors(errors);
          return;
        }

        pinSignupSurface();
        onAuthIntent?.();
        tpStripeFlashLog('signup_account_creation_start', {
          path: String(window.location.pathname || '') || null,
          email: String(email || '').trim() || null,
          username: normalizedUsername || null,
        });

        // Save username locally for later profile completion if backend doesn't persist it yet
        try { localStorage.setItem('tt_pending_signup_username', normalizedUsername); } catch {}
        try { sessionStorage.setItem('tt_pending_signup_email', email); } catch {}
        try { sessionStorage.setItem('tt_pending_signup_password', password); } catch {}

        const displayName = normalizedUsername;

        if (!usesCheckoutSignupFlow) {
          const result = await signUpWithEmail(email, password, displayName);
          if (result?.session) {
            try {
              await updateCurrentUserDisplayName(displayName);
            } catch {}
          }
          return;
        }

        try {
          onSignupCheckoutStart?.();
          tpStripeFlashLog('checkout_session_request_start', {
            path: String(window.location.pathname || '') || null,
            userEmail: email,
            plan: 'monthly',
          });
          const checkoutUrl = await createPendingCheckoutSession(email, displayName, 'monthly', brand.id, password);

          tpStripeFlashLog('checkout_session_request_success', {
            path: String(window.location.pathname || '') || null,
            checkoutUrl,
            tenantSlug: brand.id,
          });

          tpStripeFlashLog('checkout_redirect_start', {
            path: String(window.location.pathname || '') || null,
            checkoutUrl,
          });
          onCheckoutRedirectStart?.();
          clearCheckoutBannerTimers();
          window.location.href = checkoutUrl;
          return;
        } catch (signupFlowErr) {
          console.error(signupFlowErr);
          tpStripeFlashLog('signup_checkout_handoff_failed', {
            path: String(window.location.pathname || '') || null,
            error: String((signupFlowErr as Error)?.message || signupFlowErr),
          });
          onSignupCheckoutEnd?.();
          if (isUsernameTakenSignupError(signupFlowErr)) {
            setError("");
            setValidationErrors((current) => ({
              ...current,
              username: USERNAME_TAKEN_MESSAGE,
            }));
            return;
          }

          if (isEmailAlreadyExistsSignupError(signupFlowErr)) {
            const emailMessage = String((signupFlowErr as { message?: string })?.message || '').trim() || EMAIL_ALREADY_EXISTS_MESSAGE;
            setError("");
            setValidationErrors((current) => ({
              ...current,
              email: emailMessage,
            }));
            return;
          }

          setError(resolveSignupFlowErrorMessage(signupFlowErr));
          return;
        }
      } else {
        onSignupCheckoutEnd?.();
        onAuthIntent?.();
        const result = await signInWithIdentifier(email, password);
        if (result?.session) {
          void onAuthSuccess?.(result.session);
          return;
        }
        onAuthAttemptFailed?.();
      }
    } catch (err) {
      console.error(err);
      if (mode === 'signup') {
        tpStripeFlashLog('signup_submit_error', {
          path: String(window.location.pathname || '') || null,
          error: String((err as Error)?.message || err),
        });
        onSignupCheckoutEnd?.();

        if (isUsernameTakenSignupError(err)) {
          const normalizedUsername = normalizeUsernameInput(username);
          setError("");
          setUsernameAvailability({ status: "taken", username: normalizedUsername });
          setValidationErrors((current) => ({
            ...current,
            username: USERNAME_TAKEN_MESSAGE,
          }));
          return;
        }

        if (isEmailAlreadyExistsSignupError(err)) {
          const emailMessage = String((err as { message?: string })?.message || '').trim() || EMAIL_ALREADY_EXISTS_MESSAGE;
          setError("");
          setValidationErrors((current) => ({
            ...current,
            email: emailMessage,
          }));
          return;
        }

        if (isPasswordValidationSignupError(err)) {
          setError("");
          setValidationErrors((current) => ({
            ...current,
            password: PASSWORD_REQUIREMENTS_MESSAGE,
          }));
          return;
        }

        setError(GENERIC_IDENTIFIER_SIGN_UP_FAILURE_MESSAGE);
        return;
      }

      onAuthAttemptFailed?.();
      const authError = err as { message?: string };
      setError(sanitizeAuthErrorForDisplay(authError?.message || null) || "Authentication failed. Please try again.");
    } finally {
      submitInFlightRef.current = false;
      setIsEmailLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (!showGoogleAuth) return;

    onSignupCheckoutEnd?.();
    onAuthIntent?.();
    setIsGoogleLoading(true);
    setError("");

    try {
      setPostAuthRedirect("/login");
      await signInWithGoogle();
    } catch (err) {
      console.error(err);
      onAuthAttemptFailed?.();
      const authError = err as { message?: string; code?: string };
      if (authError?.code === "auth/popup-closed-by-user") {
        setError("Sign-in was cancelled.");
      } else {
        setError(sanitizeAuthErrorForDisplay(authError?.message || null) || "Google sign-in failed. Please try again.");
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const title = brand?.displayName || "your workspace";
  const signupPortalLabel = branding?.authUi?.signupPortalLabel
    || (usesCheckoutSignupFlow ? 'Start Free Trial' : 'Client Portal');
  const portalLabel = isSignUpMode
    ? signupPortalLabel
    : (brand?.authUi?.portalLabel || "Secure Access");
  const subtitle = isSignUpMode
    ? signupCopy.signupSubtitle
    : authCopy.loginSubtitle;

  // White-label wording overrides (explicit centralized tokens; never global string replacement).
  const whiteLabelHeading = isSignUpMode
    ? whiteLabel?.wording?.signupHeading
    : whiteLabel?.wording?.loginHeading;
  const whiteLabelSubtitle = isSignUpMode
    ? whiteLabel?.wording?.signupDescription
    : whiteLabel?.wording?.loginDescription;
  const displayTitle = whiteLabelHeading || title;
  const displaySubtitle = whiteLabelSubtitle || subtitle;

  const signupPropUsernameError = isSignUpMode && isUsernameTakenSignupError({ message: authErrorMessage })
    ? USERNAME_TAKEN_MESSAGE
    : "";
  const signupPropEmailError = isSignUpMode && isEmailAlreadyExistsSignupError({ message: authErrorMessage })
    ? EMAIL_ALREADY_EXISTS_MESSAGE
    : "";
  const sanitizedAuthErrorMessage = sanitizeAuthErrorForDisplay(authErrorMessage || undefined);
  const displayError = error || ((signupPropUsernameError || signupPropEmailError) ? "" : sanitizedAuthErrorMessage || "");
  const displaySuccess = authSuccessMessage || "";
  const displayNotice = noticeMessage || "";
  const recoveryMessage = pendingRecoveryStatus?.provisioningFailed
    ? 'We could not finish setting up your account. Retry to securely complete the existing trial without creating another one.'
    : pendingRecoveryStatus?.provisioningInProgress
      ? "Your trial was created and we're finishing your account setup. This usually takes only a moment."
      : "";
  const authBannerTitle = showCheckoutProgressBanner
    ? checkoutBannerTitle
    : authInProgress
      ? signInLoadingTitle
      : null;
  const authBannerSubtext = showCheckoutProgressBanner
    ? checkoutBannerSubtext
    : authInProgress
      ? signInLoadingSubtitle
      : null;
  const disableAllAuth = authInProgress || isEmailLoading || isGoogleLoading;
  const normalizedUsername = normalizeUsernameInput(username);
  const usernameFieldError = validationErrors.username || signupPropUsernameError;
  const emailFieldError = validationErrors.email || signupPropEmailError;
  const usernameMessage = usernameFieldError
    || ((usernameAvailability.status === "taken" && usernameAvailability.username === normalizedUsername) ? USERNAME_TAKEN_MESSAGE : "")
    || ((usernameAvailability.status === "checking" && usernameAvailability.username === normalizedUsername) ? "Checking availability..." : "")
    || ((usernameAvailability.status === "available" && usernameAvailability.username === normalizedUsername) ? USERNAME_AVAILABLE_MESSAGE : "")
    || USERNAME_REQUIREMENTS_MESSAGE;
  const usernameMessageClassName = usernameFieldError
    || (usernameAvailability.status === "taken" && usernameAvailability.username === normalizedUsername)
    ? "mt-1 text-xs text-red-300"
    : usernameAvailability.status === "available" && usernameAvailability.username === normalizedUsername
      ? "mt-1 text-xs text-emerald-300"
      : "mt-1 text-xs text-slate-400";
  const showSignupLogo = branding?.authUi?.showSignupLogo ?? (!isSignUpMode || usesCheckoutSignupFlow);
  const shouldShowSignupSummary = usesCheckoutSignupFlow && Boolean(String(signupCopy.signupSummary || '').trim());
  const publicRegistrationUnavailable = isSignUpMode && publicRegistrationEnabled === false;
  const pendingCheckoutCompleted = Boolean(pendingRecoveryStatus?.checkoutCompleted && !pendingRecoveryStatus?.provisioningFailed);

  const renderCreateAccountMode = () => (
    <>
      {publicRegistrationUnavailable ? (
        <div className="rounded-lg border border-[#b45309] bg-amber-500/15 px-3 py-2 text-center text-xs leading-5 text-[#fcd34d]">
          <div className="text-[15px] font-normal text-white">New registrations are currently unavailable.</div>
          <div className="mt-1 text-[#fcd34d]">This is a private partner preview. Please use the demo credentials provided to you.</div>
        </div>
      ) : null}
      {shouldShowSignupSummary ? (
        <div className="mb-2 text-center text-xs text-slate-200/80">
          {signupSummaryText}
        </div>
      ) : null}
      <button
        type="submit"
        onClick={() => {
          tpStripeFlashLog('signup_trial_button_click', {
            path: String(window.location.pathname || '') || null,
          });
        }}
        disabled={disableAllAuth || publicRegistrationUnavailable || !pendingRecoveryCheckComplete || pendingCheckoutCompleted}
        className="tenant-primary-action mt-2 flex w-full items-center justify-center rounded-lg px-4 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
        style={{ '--tenant-primary-action': isFirstPartyTradeProof ? tradeProofPrimaryColor : isBullishBears ? '#f07c00' : isBows ? '#4f8a0f' : whiteLabel?.tenantSlug === 'ztradez' ? '#528901' : whiteLabel?.tenantSlug === 'chartguys' ? '#069d52' : whiteLabel?.tenantSlug === 'stockdads' ? '#7c6e46' : whiteLabel?.tenantSlug === 'charan' ? '#800490' : whiteLabel?.tenantSlug === 'her' ? '#855158' : whiteLabel?.tenantSlug === 'demontrading' ? '#884702' : isMic ? micAccentColor : isAmericanDreamTrading ? americanDreamPrimaryColor : isProfitRaja ? '#e69502' : loginPrimaryColor, boxShadow: '0 10px 15px -3px color-mix(in srgb, var(--tenant-primary-action) 30%, transparent)' } as React.CSSProperties}
      >
        {isEmailLoading ? (usesCheckoutSignupFlow ? checkoutBannerTitle : 'Creating account...') : signupCopy.signupCtaLabel}
      </button>

      <div className="mt-2 text-center text-sm text-slate-100/90">
        {signupCopy.signupNoChargeText}
      </div>

      <hr className="my-3 border-t border-slate-700/40" />

      <div className="mt-4 text-center text-[11px] leading-5 text-slate-400/75">
        {signupCopy.signupAgreementIntro}{' '}
        <a href="/legal#terms" className="text-sky-300/90 transition hover:text-sky-200 hover:underline">{signupCopy.signupAgreementTermsLabel}</a>
        <span className="px-2 text-slate-500/70">|</span>
        <a href="/legal#privacy" className="text-sky-300/90 transition hover:text-sky-200 hover:underline">{signupCopy.signupAgreementPrivacyLabel}</a>
      </div>
      <div className="mt-4 text-center text-sm text-slate-300/90">
        {signupCopy.signupExistingAccountPrompt}{' '}
        <a
          href="/login"
          onClick={(e) => { e.preventDefault(); goToLoginMode(); }}
          className="font-semibold hover:underline"
          style={{ color: isFirstPartyTradeProof ? tradeProofPrimaryColor : isBullishBears ? '#f07c00' : isBows ? '#4f8a0f' : whiteLabel?.tenantSlug === 'ztradez' ? '#528901' : whiteLabel?.tenantSlug === 'chartguys' ? '#069d52' : whiteLabel?.tenantSlug === 'stockdads' ? '#7c6e46' : whiteLabel?.tenantSlug === 'charan' ? '#800490' : whiteLabel?.tenantSlug === 'her' ? '#855158' : whiteLabel?.tenantSlug === 'demontrading' ? '#884702' : isMic ? micAccentColor : loginPrimaryColor }}
        >
          {signupCopy.signupExistingAccountLinkLabel}
        </a>
      </div>
    </>
  );

  const renderLoginMode = () => (
    <>
      <button
        type="submit"
        disabled={disableAllAuth}
        aria-label={isEmailLoading ? 'Signing in...' : authCopy.signInButtonLabel}
          className="tenant-primary-action mt-2 flex w-full items-center justify-center rounded-lg px-4 py-3 font-semibold text-white shadow-lg shadow-black/20 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
          style={{ '--tenant-primary-action': isFirstPartyTradeProof ? tradeProofPrimaryColor : isBullishBears ? '#f07c00' : isBows ? '#4f8a0f' : whiteLabel?.tenantSlug === 'ztradez' ? '#528901' : whiteLabel?.tenantSlug === 'chartguys' ? '#069d52' : whiteLabel?.tenantSlug === 'stockdads' ? '#7c6e46' : whiteLabel?.tenantSlug === 'charan' ? '#800490' : whiteLabel?.tenantSlug === 'her' ? '#855158' : whiteLabel?.tenantSlug === 'demontrading' ? '#884702' : isMic ? micAccentColor : isAmericanDreamTrading ? americanDreamPrimaryColor : loginPrimaryColor } as React.CSSProperties}
      >
        {isEmailLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            <span>Signing in...</span>
          </>
        ) : (
          authCopy.signInButtonLabel
        )}
      </button>

      <button
        type="button"
        disabled={disableAllAuth}
        onClick={goToSignupMode}
        className="mt-3 flex w-full items-center justify-center rounded-lg border border-sky-400/40 bg-sky-500/10 px-4 py-3 font-semibold text-sky-100 transition-all duration-200 hover:scale-[1.01] hover:bg-sky-500/15 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70"
      >
        {authCopy.signupCtaLabel}
      </button>
  <div className="mt-4 text-center text-sm text-slate-400">
  Don't have an account?{" "}
  <a
    href="/signup"
    onClick={(event) => {
      event.preventDefault();
      goToSignupMode();
    }}
    className="font-semibold transition-colors hover:brightness-125 hover:underline"
    style={{ color: isFirstPartyTradeProof ? tradeProofLinkColor : isMic ? micAccentColor : loginPrimaryColor }}
  >
    Sign up
  </a>
</div>
    </>
  );



  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0b1220] via-[#0f172a] to-[#020617] p-4 font-sans text-slate-200">
      <div className="absolute top-0 left-0 h-full w-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] h-96 w-96 rounded-full bg-blue-500/10 blur-3xl opacity-30" />
        <div className="absolute bottom-[-10%] right-[-10%] h-96 w-96 rounded-full bg-indigo-500/10 blur-3xl opacity-30" />
      </div>

      <div className="relative z-10 flex w-full max-w-md flex-col items-center">
        {/* Back-to-home link intentionally removed; logo links to landing page */}

        <div className={`w-full overflow-hidden rounded-2xl border border-slate-700/50 bg-slate-900/85 shadow-2xl backdrop-blur-xl ${isFirstPartyTradeProof ? 'tp-login-card' : ''}`}>
          <div
            data-testid="auth-card-top-accent"
            aria-hidden="true"
            className="h-1 w-full"
            style={{
              background: loginStripeBackground,
              filter: isBullBearDen && shouldUseAutomaticLogoStripe ? 'brightness(1.35)' : undefined,
            }}
          />
          <div className={`${isFirstPartyTradeProof ? 'tp-login-content' : ''} p-5`}>
            <div className={`${isBearBull ? 'mb-0' : 'mb-6'} text-center`}>
              {showSignupLogo ? (
                <>
                  <LogoWrapper
                    {...(isMic || isBearBull ? {} : { href: '/', 'aria-label': 'Open landing page' })}
                    className={`relative mx-auto ${isMic ? 'mt-1' : ''} ${isBearBull ? 'mb-0 h-auto' : 'mb-1'} block w-auto`}
                    style={isBows
                      ? { paddingTop: '20px' }
                      : whiteLabel?.tenantSlug === 'fullport-university'
                        ? { paddingTop: '40px' }
                      : whiteLabel?.tenantSlug === 'bearbull'
                          ? undefined
                        : undefined}
                  >
                    {whiteLabel?.useTextLogo ? (
                      <span className={`mx-auto ${isBullishBears ? 'mb-[25px]' : 'mb-[30px]'} block max-w-full px-2 text-center text-2xl font-semibold tracking-wide text-white`}>
                        {getTextLogoName(whiteLabel.companyName)}
                      </span>
                    ) : <img
                      src={logoSrc}
                      alt={displayTitle}
                      className={`mx-auto ${isBearBull ? 'mt-[10px] mb-[20px] scale-[0.8]' : isBullishBears ? 'mb-[25px]' : 'mb-[30px]'} block w-auto object-contain drop-shadow-lg`}
                      style={logoStyle}
                    />}
                  </LogoWrapper>

                </>
              ) : (
                <div aria-hidden="true" className="mx-auto mb-1 block" style={logoStyle} />
              )}

              <div
                className={`${isBearBull ? 'mt-[30px] mb-[10px]' : 'mt-3'} inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em]`}
                style={{
                  border: isBearBull
                    ? '1px solid #e9494c'
                    : isInvestorsUnderground
                      ? '1px solid #194dbe'
                    : `1px solid color-mix(in srgb, ${portalAccentColor} 20%, transparent)`,
                  backgroundColor: isInvestorsUnderground
                    ? 'rgba(15, 23, 42, 0.85)'
                    : `color-mix(in srgb, ${portalAccentColor} 10%, transparent)`,
                  color: portalAccentColor,
                  transform: isStockVip ? 'translateY(-5px)' : undefined,
                }}
              >
                <ShieldCheck className="h-3.5 w-3.5" />
                {portalLabel}
              </div>

              <p className="mt-3 mb-6 text-sm text-slate-400">{displaySubtitle}</p>
            </div>

            {authBannerTitle ? (
              <div className="mb-4 flex items-center gap-3 rounded-lg border border-blue-500/40 bg-blue-500/10 p-3 text-sm text-blue-100" aria-live="polite">
                <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                <div>
                  <div className="font-semibold">{authBannerTitle}</div>
                  {authBannerSubtext ? (
                    <div className="text-xs text-blue-100/75">{authBannerSubtext}</div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {displayNotice ? (
              <div className="mb-4 rounded-lg border border-sky-500/40 bg-sky-500/10 p-3 text-sm text-sky-100" aria-live="polite">
                {displayNotice}
              </div>
            ) : null}

            {recoveryMessage ? (
              <div className={`mb-4 rounded-lg border p-3 text-sm ${pendingRecoveryStatus?.provisioningFailed ? 'border-red-500/50 bg-red-500/10 text-red-100' : 'border-sky-500/40 bg-sky-500/10 text-sky-100'}`} aria-live="polite" data-testid="pending-signup-recovery-message">
                {recoveryMessage}
              </div>
            ) : null}

            {displaySuccess ? (
              <div className="mb-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-100">
                {displaySuccess}
              </div>
            ) : null}

            {displayError ? (
              <div data-testid="auth-global-error" className="mb-4 flex items-start gap-2 rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-200">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{displayError}</span>
              </div>
            ) : null}

            <form onSubmit={(e) => handleSubmit(e)} className={isSignUpMode ? 'space-y-4' : 'space-y-5'}>
              {isSignUpMode ? (
                <div className="space-y-1">
                  <label className="ml-1 text-xs font-semibold uppercase tracking-wider text-slate-300">
                    Username
                  </label>
                  <div className="relative group">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500 transition-colors group-focus-within:text-blue-400">
                      <UserRound className="h-5 w-5" />
                    </div>
                    <input
                      type="text"
                      value={username}
                      onChange={handleUsernameChange}
                      onBlur={handleUsernameBlur}
                      disabled={disableAllAuth}
                      aria-invalid={usernameFieldError ? true : undefined}
                      aria-describedby="signup-username-message"
                      className="tenant-focus-control block w-full rounded-lg border border-slate-700 bg-slate-950/50 py-3 pl-10 pr-3 text-slate-200 outline-none transition-all duration-200 placeholder:text-slate-600"
                      placeholder="username"
                      required={isSignUpMode}
                    />
                  </div>
                  <div id="signup-username-message" className={usernameMessageClassName}>{usernameMessage}</div>
                </div>
              ) : null}

              <div className="space-y-1">
                <label className="ml-1 text-xs font-semibold uppercase tracking-wider text-slate-300">
                  {isSignUpMode ? 'Email Address' : 'Email or username'}
                </label>
                <div className="relative group">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500 transition-colors group-focus-within:text-blue-400">
                    <Mail className="h-5 w-5" />
                  </div>
                  <input
                    type={isSignUpMode ? 'email' : 'text'}
                    value={email}
                      onChange={handleEmailChange}
                      disabled={disableAllAuth}
                      aria-invalid={emailFieldError ? true : undefined}
                      aria-describedby={emailFieldError ? 'signup-email-message' : undefined}
                    className="tenant-focus-control block w-full rounded-lg border border-slate-700 bg-slate-950/50 py-3 pl-10 pr-3 text-slate-200 outline-none transition-all duration-200 placeholder:text-slate-600"
                    placeholder={isSignUpMode ? 'trader@example.com' : 'Enter your email or username'}
                    required={!isSignUpMode}
                  />
                </div>
                {emailFieldError ? (
                  <div id="signup-email-message" className="mt-1 text-xs text-red-300">
                    {emailFieldError}
                  </div>
                ) : null}
              </div>

              {isSignUpMode && usesFullSignupFields ? (
                <>
                  <div className="space-y-1">
                    <label className="ml-1 text-xs font-semibold uppercase tracking-wider text-slate-300">
                      Password
                    </label>
                    <div className="relative group">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500 transition-colors group-focus-within:text-blue-400">
                        <Lock className="h-5 w-5" />
                      </div>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setError("");
                          setValidationErrors((current) => {
                            if (!current.password && !current.confirmPassword) return current;
                            const nextErrors = { ...current };
                            delete nextErrors.password;
                            delete nextErrors.confirmPassword;
                            return nextErrors;
                          });
                        }}
                        disabled={disableAllAuth}
                        aria-invalid={validationErrors.password ? true : undefined}
                        aria-describedby={validationErrors.password ? 'signup-password-message' : undefined}
                        className="tenant-focus-control block w-full rounded-lg border border-slate-700 bg-slate-950/50 py-3 pl-10 pr-10 text-slate-200 outline-none transition-all duration-200 placeholder:text-slate-600"
                        placeholder="••••••••"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 transition-colors hover:text-slate-300 focus:outline-none"
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    {validationErrors.password ? (
                      <div id="signup-password-message" className="mt-1 text-xs text-red-300">
                        {validationErrors.password}
                      </div>
                    ) : null}
                  </div>

                  <div className="space-y-1">
                    <label className="ml-1 text-xs font-semibold uppercase tracking-wider text-slate-300">
                      Confirm Password
                    </label>
                    <div className="relative group">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500 transition-colors group-focus-within:text-blue-400">
                        <Lock className="h-5 w-5" />
                      </div>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setError("");
                          setValidationErrors((current) => {
                            if (!current.confirmPassword) return current;
                            const nextErrors = { ...current };
                            delete nextErrors.confirmPassword;
                            return nextErrors;
                          });
                        }}
                        disabled={disableAllAuth}
                        aria-invalid={validationErrors.confirmPassword ? true : undefined}
                        aria-describedby={validationErrors.confirmPassword ? 'signup-confirm-password-message' : undefined}
                      className="tenant-focus-control block w-full rounded-lg border border-slate-700 bg-slate-950/50 py-3 pl-10 pr-3 text-slate-200 outline-none transition-all duration-200 placeholder:text-slate-600"
                        placeholder="••••••••"
                        required
                      />
                    </div>
                    {validationErrors.confirmPassword ? (
                      <div id="signup-confirm-password-message" className="mt-1 text-xs text-red-300">
                        {validationErrors.confirmPassword}
                      </div>
                    ) : null}
                  </div>
                </>
              ) : null}

              {!isSignUpMode ? (
                <div className="space-y-1">
                  <div className="ml-1 flex items-center justify-between">
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-300">
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => onForgotPassword?.()}
                      className="text-xs text-blue-400 transition-colors hover:text-blue-300"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <div className="relative group">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500 transition-colors group-focus-within:text-blue-400">
                      <Lock className="h-5 w-5" />
                    </div>

                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                        disabled={disableAllAuth}
                      className="tenant-focus-control block w-full rounded-lg border border-slate-700 bg-slate-950/50 py-3 pl-10 pr-10 text-slate-200 outline-none transition-all duration-200 placeholder:text-slate-600"
                      placeholder="••••••••"
                      required
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500 transition-colors hover:text-slate-300 focus:outline-none"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>

                  {validationErrors.password ? <div className="mt-1 text-xs text-red-300">{validationErrors.password}</div> : null}
                </div>
              ) : null}

              {isSignUpMode ? renderCreateAccountMode() : renderLoginMode()}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
